import {
  Box,
  Container,
  Grid,
  Stack,
  Typography,
  Link,
  IconButton,
} from "@mui/material";
import { styled} from "@mui/material/styles";
import {
  FaFacebook,
  FaTwitter,
  FaInstagram,
  FaYoutube,
  FaEnvelope,
  FaWhatsapp,
  FaHeart,
  FaGraduationCap,
  FaShieldAlt,
  FaRocket,
} from "react-icons/fa";

// ─── Animations ───────────────────────────────────────────────────────────────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,400;12..96,600;12..96,700;12..96,800;12..96,900&display=swap');

  @keyframes glowPulse {
    0%, 100% { opacity: 1; box-shadow: 0 0 6px #10b981; }
    50%       { opacity: 0.4; box-shadow: 0 0 14px #10b981; }
  }
  @keyframes orbDrift {
    0%, 100% { transform: translate(0, 0) scale(1); }
    33%       { transform: translate(30px, -20px) scale(1.06); }
    66%       { transform: translate(-15px, 12px) scale(0.96); }
  }
  @keyframes lineExpand {
    from { transform: scaleX(0); }
    to   { transform: scaleX(1); }
  }
  @keyframes gradShift {
    0%   { background-position: 0% 50%; }
    50%  { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  @keyframes floatUp {
    0%, 100% { transform: translateY(0); }
    50%       { transform: translateY(-4px); }
  }
  @keyframes borderGlow {
    0%, 100% { border-color: rgba(2,136,209,0.2); }
    50%       { border-color: rgba(2,136,209,0.5); }
  }
`;

// ─── Styled Components ────────────────────────────────────────────────────────
const FooterWrap = styled(Box)(() => ({
  background: "linear-gradient(180deg, #080f1e 0%, #050c1a 60%, #030710 100%)",
  color: "#fff",
  position: "relative",
  overflow: "hidden",
  fontFamily: "'Bricolage Grotesque', sans-serif",
}));

const GlowOrb = styled(Box)(
  ({
    size = 400,
    top,
    left,
    right,
    bottom,
    color = "rgba(2,136,209,0.12)",
  }) => ({
    position: "absolute",
    width: size,
    height: size,
    borderRadius: "50%",
    background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
    top,
    left,
    right,
    bottom,
    pointerEvents: "none",
    animation: "orbDrift 18s ease-in-out infinite",
  })
);

const TopBar = styled(Box)(() => ({
  height: 4,
  background:
    "linear-gradient(90deg, #0288D1 0%, #0ea5e9 30%, #7c3aed 60%, #0288D1 100%)",
  backgroundSize: "200% 100%",
  animation: "gradShift 6s ease infinite",
  boxShadow: "0 0 20px rgba(2,136,209,0.4)",
}));

const LogoText = styled(Typography)(() => ({
  fontFamily: "'Bricolage Grotesque', sans-serif",
  fontWeight: 900,
  letterSpacing: "-1.5px",
  background: "linear-gradient(135deg, #0ea5e9 0%, #60a5fa 40%, #a78bfa 100%)",
  backgroundSize: "200% 200%",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  animation: "gradShift 5s ease infinite",
  display: "inline-block",
}));

const SectionHeading = styled(Typography)(() => ({
  fontSize: "0.62rem",
  fontWeight: 800,
  color: "rgba(255,255,255,0.25)",
  textTransform: "uppercase",
  letterSpacing: "1.4px",
  marginBottom: 22,
  fontFamily: "'Bricolage Grotesque', sans-serif",
  display: "flex",
  alignItems: "center",
  gap: 8,
  "&::after": {
    content: '""',
    flex: 1,
    height: 1,
    background: "linear-gradient(90deg, rgba(255,255,255,0.1), transparent)",
    borderRadius: 99,
  },
}));

const FooterLink = styled(Link)(() => ({
  color: "rgba(255,255,255,0.45)",
  fontSize: "0.875rem",
  fontWeight: 500,
  textDecoration: "none",
  fontFamily: "'Bricolage Grotesque', sans-serif",
  display: "flex",
  alignItems: "center",
  gap: 8,
  transition: "all 0.22s ease",
  paddingLeft: 0,
  "& .dot": {
    width: 4,
    height: 4,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.2)",
    flexShrink: 0,
    transition: "all 0.22s ease",
  },
  "&:hover": {
    color: "#0ea5e9",
    transform: "translateX(6px)",
    "& .dot": { background: "#0ea5e9", boxShadow: "0 0 8px #0ea5e9" },
  },
}));

const ContactCard = styled(Link)(() => ({
  display: "flex",
  alignItems: "center",
  gap: 14,
  textDecoration: "none",
  padding: "14px 16px",
  borderRadius: 14,
  border: "1px solid rgba(255,255,255,0.06)",
  background: "rgba(255,255,255,0.025)",
  transition: "all 0.22s ease",
  "&:hover": {
    background: "rgba(255,255,255,0.05)",
    borderColor: "rgba(2,136,209,0.3)",
    transform: "translateY(-2px)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
  },
}));

const ContactIconBox = styled(Box)(({ glow }) => ({
  width: 40,
  height: 40,
  borderRadius: 11,
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: glow ? "rgba(2,136,209,0.14)" : "rgba(37,211,102,0.1)",
  border: `1px solid ${
    glow ? "rgba(2,136,209,0.22)" : "rgba(37,211,102,0.18)"
  }`,
  animation: "borderGlow 3s ease-in-out infinite",
}));

const SocialBtn = styled(IconButton)(({ hovercolor, hoverBg }) => ({
  color: "rgba(255,255,255,0.35)",
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.07)",
  borderRadius: "10px",
  width: 38,
  height: 38,
  transition: "all 0.22s ease",
  "&:hover": {
    color: hovercolor,
    background: hoverBg,
    borderColor: hovercolor,
    transform: "translateY(-4px) scale(1.08)",
    boxShadow: `0 8px 20px ${hoverBg}`,
    animation: "floatUp 0s",
  },
}));

const StatPill = styled(Box)(({ color }) => ({
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "10px 16px",
  borderRadius: 12,
  background: `${color}10`,
  border: `1px solid ${color}22`,
  flex: 1,
}));

const BottomDivider = styled(Box)(() => ({
  height: 1,
  background:
    "linear-gradient(90deg, transparent, rgba(255,255,255,0.07) 30%, rgba(255,255,255,0.07) 70%, transparent)",
  margin: "0",
}));

// ─── Component ────────────────────────────────────────────────────────────────
const HomeFooter = () => {
  const currentYear = new Date().getFullYear();

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: STYLES }} />
      <FooterWrap component="footer">
        {/* Ambient orbs */}
        <GlowOrb size={500} top="-15%" left="-8%" color="rgba(2,136,209,0.1)" />
        <GlowOrb
          size={400}
          bottom="-10%"
          right="-6%"
          color="rgba(124,58,237,0.08)"
        />
        <GlowOrb
          size={300}
          top="40%"
          left="40%"
          color="rgba(14,165,233,0.05)"
        />

        {/* Dot texture */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            opacity: 0.025,
            backgroundImage:
              "radial-gradient(circle, #fff 1px, transparent 1px)",
            backgroundSize: "26px 26px",
            pointerEvents: "none",
          }}
        />

        {/* Animated top bar */}
        <TopBar />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 ,paddingTop:"100px"}}>
          {/* ── CTA Banner ── */}
   

          {/* ── 3 stat pills ── */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            sx={{ mb: { xs: 6, md: 8 } }}
          >
            {[
              {
                icon: <FaGraduationCap size={14} color="#0ea5e9" />,
                label: "10,000+ Students",
                color: "#0ea5e9",
              },
              {
                icon: <FaShieldAlt size={14} color="#10b981" />,
                label: "5+ Exam Bodies Covered",
                color: "#10b981",
              },
              {
                icon: <FaRocket size={14} color="#a78bfa" />,
                label: "Smart CBT Platform",
                color: "#a78bfa",
              },
            ].map((s, i) => (
              <StatPill key={i} color={s.color}>
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: "9px",
                    background: `${s.color}18`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {s.icon}
                </Box>
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: 700,
                    color: "rgba(255,255,255,0.6)",
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                  }}
                >
                  {s.label}
                </Typography>
              </StatPill>
            ))}
          </Stack>

          {/* ── Main Footer Grid ── */}
          <Grid
            container
            spacing={{ xs: 4, md: 6 }}
            sx={{ pb: { xs: 6, md: 8 } }}
          >
            {/* Brand column */}
            <Grid item xs={12} md={4.5}>
              <Stack spacing={3}>
                {/* Logo + tagline */}
                <Box>
                  <LogoText
                    variant="h3"
                    sx={{ fontSize: { xs: "2rem", md: "2.4rem" }, mb: 0.6 }}
                  >
                    LiteUpper
                  </LogoText>
                  <Typography
                    sx={{
                      fontSize: "0.62rem",
                      fontWeight: 700,
                      color: "rgba(255,255,255,0.25)",
                      textTransform: "uppercase",
                      letterSpacing: "1.4px",
                    }}
                  >
                    Your Trusted Exam Prep Partner
                  </Typography>
                </Box>

                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.42)",
                    fontSize: "0.875rem",
                    lineHeight: 1.85,
                    maxWidth: 320,
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                  }}
                >
                  Empowering students and institutions across Africa with smart,
                  secure digital assessments and curated exam resources.
                </Typography>

                {/* Social row */}
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.6rem",
                      fontWeight: 700,
                      color: "rgba(255,255,255,0.2)",
                      textTransform: "uppercase",
                      letterSpacing: "1px",
                      mb: 1.5,
                      fontFamily: "'Bricolage Grotesque', sans-serif",
                    }}
                  >
                    Follow us
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    {[
                      {
                        href: "https://facebook.com",
                        icon: <FaFacebook size={14} />,
                        hovercolor: "#1877f2",
                        hoverBg: "rgba(24,119,242,0.15)",
                      },
                      {
                        href: "https://twitter.com",
                        icon: <FaTwitter size={14} />,
                        hovercolor: "#1da1f2",
                        hoverBg: "rgba(29,161,242,0.15)",
                      },
                      {
                        href: "https://instagram.com",
                        icon: <FaInstagram size={14} />,
                        hovercolor: "#e4405f",
                        hoverBg: "rgba(228,64,95,0.15)",
                      },
                      {
                        href: "https://youtube.com",
                        icon: <FaYoutube size={14} />,
                        hovercolor: "#ff0000",
                        hoverBg: "rgba(255,0,0,0.12)",
                      },
                    ].map((s, i) => (
                      <SocialBtn
                        key={i}
                        href={s.href}
                        target="_blank"
                        size="small"
                        hovercolor={s.hovercolor}
                        hoverbg={s.hoverBg}
                        sx={{
                          "&:hover": {
                            color: s.hovercolor,
                            background: s.hoverBg,
                            borderColor: s.hovercolor,
                            transform: "translateY(-4px) scale(1.08)",
                            boxShadow: `0 8px 20px ${s.hoverBg}`,
                          },
                        }}
                      >
                        {s.icon}
                      </SocialBtn>
                    ))}
                  </Stack>
                </Box>
              </Stack>
            </Grid>

            {/* Company links */}
            <Grid item xs={6} md={2.5}>
              <SectionHeading>Company</SectionHeading>
              <Stack spacing={1.8}>
                {[
                  { label: "About Us", href: "#about" },
                  { label: "Privacy Policy", href: "/privacy-policy" },
                  { label: "Terms of Use", href: "#" },
                  { label: "Contact", href: "#contact" },
                  { label: "Our Proposal", href: "/LiteupperProposal" },
                ].map((link) => (
                  <FooterLink key={link.label} href={link.href}>
                    <Box className="dot" />
                    {link.label}
                  </FooterLink>
                ))}
              </Stack>
            </Grid>

            {/* Contact */}
            <Grid item xs={12} md={5}>
              <SectionHeading>Get in Touch</SectionHeading>
              <Stack spacing={1.5}>
                <ContactCard href="mailto:liteupper@gmail.com">
                  <ContactIconBox glow={1}>
                    <FaEnvelope size={14} color="#0ea5e9" />
                  </ContactIconBox>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "0.58rem",
                        color: "rgba(255,255,255,0.25)",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.8px",
                        fontFamily: "'Bricolage Grotesque', sans-serif",
                        mb: 0.2,
                      }}
                    >
                      Email
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.85rem",
                        color: "rgba(255,255,255,0.7)",
                        fontWeight: 700,
                        fontFamily: "'Bricolage Grotesque', sans-serif",
                      }}
                    >
                      liteupper@gmail.com
                    </Typography>
                  </Box>
                </ContactCard>

                <ContactCard href="https://wa.me/2349061709128" target="_blank">
                  <ContactIconBox glow={0}>
                    <FaWhatsapp size={15} color="#25D366" />
                  </ContactIconBox>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "0.58rem",
                        color: "rgba(255,255,255,0.25)",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.8px",
                        fontFamily: "'Bricolage Grotesque', sans-serif",
                        mb: 0.2,
                      }}
                    >
                      WhatsApp
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.85rem",
                        color: "rgba(255,255,255,0.7)",
                        fontWeight: 700,
                        fontFamily: "'Bricolage Grotesque', sans-serif",
                      }}
                    >
                      +234 906 170 9128
                    </Typography>
                  </Box>
                </ContactCard>

                {/* Live support badge */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    px: 2,
                    py: 1.4,
                    borderRadius: 3,
                    background: "rgba(16,185,129,0.06)",
                    border: "1px solid rgba(16,185,129,0.14)",
                    mt: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: "#10b981",
                      boxShadow: "0 0 0 0 #10b981",
                      flexShrink: 0,
                      animation: "glowPulse 2s ease-in-out infinite",
                    }}
                  />
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "0.76rem",
                        fontWeight: 700,
                        color: "#10b981",
                        fontFamily: "'Bricolage Grotesque', sans-serif",
                      }}
                    >
                      Support Online Now
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.68rem",
                        color: "rgba(255,255,255,0.28)",
                        fontFamily: "'Bricolage Grotesque', sans-serif",
                      }}
                    >
                      Typically responds within minutes
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </Grid>
          </Grid>

          {/* ── Divider ── */}
          <BottomDivider />

          {/* ── Bottom bar ── */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems="center"
            justifyContent="space-between"
            gap={1.5}
            sx={{ py: 3.5 }}
          >
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: "rgba(255,255,255,0.22)",
                fontWeight: 500,
                fontFamily: "'Bricolage Grotesque', sans-serif",
              }}
            >
              © {currentYear} LiteUpper. All rights reserved.
            </Typography>

            <Stack direction="row" alignItems="center" spacing={0.8}>
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  color: "rgba(255,255,255,0.2)",
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                }}
              >
                Made with
              </Typography>
              <FaHeart size={10} color="#e91e63" />
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  color: "rgba(255,255,255,0.2)",
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                }}
              >
                in Nigeria
              </Typography>
              <Box component="span" sx={{ fontSize: "0.88rem" }}>
                🇳🇬
              </Box>
            </Stack>

            <Stack direction="row" spacing={2.5}>
              {["Privacy", "Terms", "Cookies"].map((item) => (
                <Link
                  key={item}
                  href="#"
                  underline="none"
                  sx={{
                    fontSize: "0.72rem",
                    color: "rgba(255,255,255,0.22)",
                    fontWeight: 600,
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                    transition: "color 0.2s",
                    "&:hover": { color: "#0ea5e9" },
                  }}
                >
                  {item}
                </Link>
              ))}
            </Stack>
          </Stack>
        </Container>
      </FooterWrap>
    </>
  );
};

export default HomeFooter;
